import { ApolloError, ApolloQueryResult, NetworkStatus } from "@apollo/client";
import { createContext } from "react";
import {
  CompanyRole,
  ProjectDetailFragment,
  SecurityLevel,
  UserContextQuery,
  VendorProjectFragment,
} from "@/__generated__/graphql.ts";

export type SystemAnnouncement = UserContextQuery["announcements"][number];
export type NullableCurrentUser = UserContextQuery["currentUser"];

export type CurrentUser = NonNullable<NullableCurrentUser>;
export type NullableCurrentUserOffice = CurrentUser["office"];
export type CurrentUserOffice = NonNullable<NullableCurrentUserOffice>;
export type Company = CurrentUserOffice["company"];

export interface UserContextShape {
  isAuthenticated: boolean;
  systemAnnouncements: SystemAnnouncement[];
  currentUser: NullableCurrentUser;
  office: NullableCurrentUserOffice;
  role: Company["role"] | null;
  loading: boolean;
  error?: ApolloError;
  refetchUser(): Promise<ApolloQueryResult<UserContextQuery | null>>;
  refetchingUser: boolean;
  currentProject: ProjectDetailFragment | null;
  currentVendorProject: VendorProjectFragment | null;
  setCurrentProject: (projectId: string | null) => void;
  isHotspringVendorProfile: boolean;
  isHotspringClientProfile: boolean;
  pastMidnight: boolean;
}

export const UserContext = createContext<UserContextShape>({
  loading: true,
  isAuthenticated: false,
  systemAnnouncements: [],
  currentUser: null,
  office: null,
  role: null,
  refetchUser: async () => {
    return {
      data: null,
      loading: false,
      stale: false,
      networkStatus: NetworkStatus.ready,
    };
  },
  refetchingUser: false,
  currentProject: null,
  currentVendorProject: null,
  setCurrentProject: () => {},
  isHotspringClientProfile: false,
  isHotspringVendorProfile: false,
  pastMidnight: false,
});

export const isDefaultVendor = (
  role: CompanyRole,
  user?: NullableCurrentUser,
) => {
  return (
    role === CompanyRole.Vendor && user?.office?.company?.profile?.isDefault
  );
};

export const isDefaultVendorClientProfile = (
  role: CompanyRole,
  user?: NullableCurrentUser,
) => {
  return (
    role === CompanyRole.Client &&
    user?.subcontractingUser?.office?.company.profile.isDefault
  );
};

export const isSubcontractedVendor = (
  role: CompanyRole,
  project: {
    id: string;
    securityLevel: SecurityLevel;
    sourceProject?: null | {
      id: string;
    };
  },
) => {
  return role === CompanyRole.Vendor && !!project.sourceProject;
};
